import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import {
  Image as ImageIcon, Upload, RotateCcw, Loader2, AlertTriangle, CheckCircle2,
  FileText, RefreshCw,
} from "lucide-react";

const MB = 1024 * 1024;
const MAX_BYTES = 5 * MB;
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/jpg"];
const ALLOWED_EXTS = [".png", ".jpg", ".jpeg"];

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL"); } catch { return iso; }
}

/**
 * Document Branding — beheert het logo dat op factuur/offerte/document-PDF's
 * verschijnt. Owner/Admin mag uploaden en resetten; Manager/Medewerker zien
 * een read-only preview.
 */
export default function DocumentLogoSection() {
  const { user } = useAuth();
  const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState("");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch the current logo as a blob URL (auth header required, so we can't
  // use a plain <img src=...>). Object URLs are revoked when replaced.
  const fetchPreview = useCallback(async (metaPayload) => {
    if (!metaPayload?.logo_url) {
      setPreviewUrl(null);
      return;
    }
    try {
      const res = await adminApi.get(metaPayload.logo_url.replace(/^\/api/, ""), {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: metaPayload.mime || "image/png" });
      setPreviewUrl((prev) => {
        if (prev) {
          try { URL.revokeObjectURL(prev); } catch (e) { void e; }
        }
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      setPreviewUrl(null);
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/document-branding/logo/meta");
      setMeta(res.data);
      await fetchPreview(res.data);
    } catch (e) {
      setError(formatApiError(e) || "Logo-informatie kon niet worden geladen.");
    } finally {
      setLoading(false);
    }
  }, [fetchPreview]);

  // Live factuur-preview: rendert sample factuur PDF als PNG met huidige logo.
  // Cache-busting via query param zodat iOS Safari (en andere browsers die
  // `no-store` lossly honoren) gegarandeerd opnieuw fetchen ipv image-cache te
  // hergebruiken.
  const fetchInvoicePreview = useCallback(async () => {
    setInvoicePreviewLoading(true);
    setInvoicePreviewError("");
    // Maak de oude preview onmiddellijk leeg zodat de gebruiker visueel
    // feedback krijgt dat er een refresh bezig is.
    setInvoicePreviewUrl((prev) => {
      if (prev) {
        try { URL.revokeObjectURL(prev); } catch (e) { void e; }
      }
      return null;
    });
    try {
      const ts = Date.now();
      const res = await adminApi.get(
        `/admin/document-branding/invoice-preview.png?_ts=${ts}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "image/png" });
      setInvoicePreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setInvoicePreviewError(formatApiError(e) || "Factuur-preview kon niet worden gegenereerd.");
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchInvoicePreview(); }, [fetchInvoicePreview]);
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) { void e; }
      }
      if (invoicePreviewUrl) {
        try { URL.revokeObjectURL(invoicePreviewUrl); } catch (e) { void e; }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setUploadFile(null);
      setUploadPreview("");
      return;
    }
    if (!ALLOWED_MIME.includes(file.type) && !ALLOWED_EXTS.some(ext => file.name.toLowerCase().endsWith(ext))) {
      toast.error("Alleen PNG of JPG bestanden zijn toegestaan.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Bestand te groot. Maximaal ${MAX_BYTES / MB} MB.`);
      e.target.value = "";
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result || "");
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      const res = await adminApi.post("/admin/document-branding/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeta(res.data);
      await fetchPreview(res.data);
      await fetchInvoicePreview(); // expliciet awaiten zodat order vast staat
      toast.success("Documentlogo is bijgewerkt. Nieuwe facturen en documenten gebruiken vanaf nu dit logo.");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadPreview("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e) || "Upload is mislukt.");
    } finally {
      setUploadBusy(false);
    }
  };

  const handleReset = async () => {
    setResetBusy(true);
    try {
      const res = await adminApi.post("/admin/document-branding/logo/reset", {});
      setMeta(res.data);
      await fetchPreview(res.data);
      await fetchInvoicePreview(); // expliciet awaiten zodat order vast staat
      toast.success("Logo gereset naar het standaard 5999Cargo-logo.");
      setResetOpen(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Reset is mislukt.");
    } finally {
      setResetBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-8 flex items-center justify-center" data-testid="document-logo-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 p-6 text-red-700 flex items-start gap-3" data-testid="document-logo-error">
        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-chivo font-bold">Logo-informatie niet beschikbaar</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const isCustom = !!meta?.has_custom_logo;
  const logoUrl = previewUrl;

  return (
    <div className="space-y-4" data-testid="document-logo-section">
      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
              <ImageIcon className="w-5 h-5" /> Documentlogo
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Dit logo wordt gebruikt op alle PDF-documenten (verkoopfacturen, offertes en branded documenten).
            </p>
          </div>
          {isOwnerOrAdmin && (
            <div className="flex gap-2">
              <Button
                onClick={() => setUploadOpen(true)}
                className="rounded-none bg-[#0A192F] hover:bg-[#0c1e3a] text-white font-chivo font-bold"
                data-testid="document-logo-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Nieuw logo uploaden
              </Button>
              {isCustom && (
                <Button
                  variant="outline"
                  onClick={() => setResetOpen(true)}
                  className="rounded-none font-chivo"
                  data-testid="document-logo-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset naar standaard
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Preview + meta */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          <div
            className="flex items-center justify-center bg-gray-50 border border-gray-200 p-6 min-h-[160px]"
            data-testid="document-logo-preview-wrap"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Documentlogo"
                className="max-w-full max-h-[140px] object-contain"
                data-testid="document-logo-preview-img"
              />
            ) : (
              <div className="text-gray-400 text-sm flex flex-col items-center gap-2">
                <ImageIcon className="w-8 h-8" />
                Geen logo beschikbaar
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {isCustom ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-chivo font-bold bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" /> Custom logo actief
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-chivo font-bold bg-gray-100 text-gray-700">
                  Standaardlogo actief
                </span>
              )}
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Bestandsnaam</dt>
                <dd className="font-chivo text-[#0A192F]" data-testid="document-logo-filename">
                  {meta?.filename || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Bestandstype</dt>
                <dd className="font-chivo text-[#0A192F]">{(meta?.mime || "—").toUpperCase()}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Bestandsgrootte</dt>
                <dd className="font-chivo text-[#0A192F]">{fmtSize(meta?.size_bytes)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Geüpload op</dt>
                <dd className="font-chivo text-[#0A192F]">{fmtDate(meta?.uploaded_at)}</dd>
              </div>
              {meta?.uploaded_by && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Geüpload door</dt>
                  <dd className="font-chivo text-[#0A192F]">{meta.uploaded_by}</dd>
                </div>
              )}
            </dl>
            {!isOwnerOrAdmin && (
              <p className="text-xs text-gray-500 mt-2">
                Alleen Owner/Admin kunnen het documentlogo wijzigen.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Maximaal {MAX_BYTES / MB} MB · PNG of JPG · Aspect ratio wordt automatisch behouden ·
              Aanbevolen breedte ≥ 500px voor een scherpe PDF-weergave.
            </p>
          </div>
        </div>
      </div>

      {/* Live factuur-preview */}
      <div className="bg-white border border-gray-200" data-testid="document-logo-invoice-preview">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-chivo font-bold text-base text-[#0A192F] flex items-center gap-2">
              <FileText className="w-4 h-4" /> Live factuur-preview
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Zo ziet je documentlogo eruit op een echte factuur. De preview ververst automatisch
              na elke logo-wijziging.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchInvoicePreview}
            disabled={invoicePreviewLoading}
            className="rounded-none font-chivo text-xs"
            data-testid="document-logo-invoice-preview-refresh"
          >
            {invoicePreviewLoading ? (
              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3 mr-2" />
            )}
            Vernieuwen
          </Button>
        </div>
        <div className="p-4 bg-gray-50 flex items-center justify-center min-h-[480px]">
          {invoicePreviewLoading ? (
            <div className="flex flex-col items-center gap-3 text-gray-500 text-sm">
              <Loader2 className="w-8 h-8 animate-spin" />
              Preview wordt gegenereerd...
            </div>
          ) : invoicePreviewError ? (
            <div className="flex items-start gap-2 text-red-700 text-sm max-w-md text-center">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{invoicePreviewError}</span>
            </div>
          ) : invoicePreviewUrl ? (
            <img
              src={invoicePreviewUrl}
              alt="Factuur-preview"
              className="max-w-full max-h-[700px] object-contain shadow-md border border-gray-300"
              data-testid="document-logo-invoice-preview-img"
            />
          ) : (
            <div className="text-gray-400 text-sm flex flex-col items-center gap-2">
              <FileText className="w-8 h-8" />
              Nog geen preview beschikbaar
            </div>
          )}
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploadBusy) setUploadOpen(o); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="document-logo-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold">Nieuw documentlogo uploaden</DialogTitle>
            <DialogDescription className="text-sm">
              Kies een PNG of JPG bestand (max {MAX_BYTES / MB} MB). Het logo wordt automatisch op
              alle nieuwe facturen en offertes toegepast.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_EXTS.join(",")}
              onChange={handleFilePick}
              className="block w-full text-sm border border-gray-200 p-2"
              data-testid="document-logo-file-input"
            />
            {uploadPreview && (
              <div className="bg-gray-50 border border-gray-200 p-4 flex items-center justify-center">
                <img
                  src={uploadPreview}
                  alt="Preview"
                  className="max-w-full max-h-[140px] object-contain"
                  data-testid="document-logo-upload-preview"
                />
              </div>
            )}
            {uploadFile && (
              <p className="text-xs text-gray-600">
                {uploadFile.name} · {fmtSize(uploadFile.size)}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { if (!uploadBusy) { setUploadOpen(false); setUploadFile(null); setUploadPreview(""); } }}
              disabled={uploadBusy}
              className="rounded-none"
              data-testid="document-logo-upload-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploadBusy}
              className="rounded-none bg-[#0A192F] hover:bg-[#0c1e3a] text-white font-chivo font-bold"
              data-testid="document-logo-upload-confirm"
            >
              {uploadBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploadBusy ? "Bezig met uploaden..." : "Logo uploaden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetBusy) setResetOpen(o); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="document-logo-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold">Logo resetten naar standaard?</DialogTitle>
            <DialogDescription className="text-sm">
              Het huidige uploaded logo wordt verwijderd en vervangen door het standaard 5999Cargo-logo.
              Bestaande PDF&apos;s blijven ongewijzigd.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { if (!resetBusy) setResetOpen(false); }}
              disabled={resetBusy}
              className="rounded-none"
              data-testid="document-logo-reset-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleReset}
              disabled={resetBusy}
              variant="destructive"
              className="rounded-none font-chivo font-bold"
              data-testid="document-logo-reset-confirm"
            >
              {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              {resetBusy ? "Bezig..." : "Ja, reset naar standaard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
